/**
 * Conversation Feedback Service - Handles per-message thumbs up/down feedback
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from '../auth/msalAuth';
import logger from '../logger/logger';

export interface ConversationFeedbackRequest {
  query: string;
  response: string;
  rating: 'thumbs_up' | 'thumbs_down';
  feedback: string;
  rag_type: string;
  /** HR RAG only. JSON-encoded array from the rated message; '' when absent. */
  citations?: string;
  /** HR RAG only. JSON-encoded array from the rated message; '' when absent. */
  sources?: string;
}

export interface ConversationFeedbackResponse {
  success: boolean;
  message: string;
  feedback_id: string;
}

/** Per-attempt timeout before treating the request as a network failure. */
const REQUEST_TIMEOUT_MS = 8_000;
/** How many times to retry after a timeout or network error (not server errors). */
const MAX_RETRIES = 2;

/**
 * Submit per-message conversation feedback to APIM endpoint.
 * Automatically retries up to MAX_RETRIES times on network errors or timeouts.
 * Server-side errors (4xx / 5xx) are surfaced immediately without retrying.
 */
export async function submitConversationFeedback(
  msalInstance: IPublicClientApplication,
  request: ConversationFeedbackRequest
): Promise<ConversationFeedbackResponse> {
  const accessToken = await getAccessTokenHelper(msalInstance);
  const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';
  const endpoint = `${apimBaseUrl}/nav-rag-api/feedback-rag`;

  logger.log('[ConversationFeedback] Submitting feedback:', {
    endpoint,
    rating: request.rating,
    rag_type: request.rag_type,
  });

  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;

      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      const isNetworkError = error instanceof TypeError;

      if ((!isTimeout && !isNetworkError) || attempt === MAX_RETRIES) {
        console.error('[ConversationFeedback] Submission failed:', error);
        throw error;
      }

      const retryDelay = Math.min(500 * Math.pow(2, attempt), 2_000);
      logger.log(
        `[ConversationFeedback] Attempt ${attempt + 1} failed (${isTimeout ? 'timeout' : 'network error'}), retrying in ${retryDelay}ms…`
      );
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      continue;
    }

    // Server responded — parse once and return/throw without retrying.
    logger.log('[ConversationFeedback] Response status:', response.status, response.statusText);

    const responseText = await response.text();
    let responseData: any;
    try {
      responseData = JSON.parse(responseText);
    } catch {
      const err = new Error(
        `Server returned non-JSON response (${response.status}): ${responseText.substring(0, 200)}`
      );
      console.error('[ConversationFeedback] Submission failed:', err);
      throw err;
    }

    if (!response.ok) {
      const err = new Error(
        responseData?.error?.message || `Failed to submit conversation feedback: ${response.status}`
      );
      console.error('[ConversationFeedback] Submission failed:', err);
      throw err;
    }

    logger.log('[ConversationFeedback] Success:', responseData);
    return responseData as ConversationFeedbackResponse;
  }

  throw lastError;
}
